import React from 'react';
import { useTranslation } from 'react-i18next';

import type { ElasticVehicle } from '../../@types/ElasticVehicle';
import { resolveCarValuationRoute } from '../../routes/utils';
import Alert from '../../styled-components/components/Alert';
import { Button } from '../../styled-components/components/Button';
import { Column, Columns } from '../../styled-components/components/Columns';
import Content from '../../styled-components/components/Content';
import { H2 } from '../../styled-components/components/Heading';
import { Label } from '../../styled-components/components/Label';
import { MaxWidth } from '../../styled-components/components/MaxWidth';
import { ProductGrid, ProductGridItem } from '../../styled-components/components/ProductGrid';
import { Sheet } from '../../styled-components/components/Sheet';
import { Stack, StackSmall } from '../../styled-components/components/Stack';
import Link from '../link';
import ResultSetItem from './result-set-item';

interface ItemsProps {
  data: ElasticVehicle[];
  from: number;
  to: number;
  title?: string;
}

const Items = ({ data, from, to, title }: ItemsProps) => {
  return (
    <>
      {data.slice(from, to).map((document, index) => (
        <ResultSetItem
          key={document.id}
          document={document}
          index={index}
          title={title}
          testId={`product-card-${index}`}
        />
      ))}
    </>
  );
};

interface ResultSetContainerProps {
  data: ElasticVehicle[];
  title?: string;
  shouldHideAds?: boolean;
}

const ResultSetContainer = ({ data, title }: ResultSetContainerProps) => {
  const { t } = useTranslation(['search']);

  if (!data || data.length === 0) {
    return <Alert tone="info">{t('yourSearchResultedInNoResults', { ns: 'search' })}</Alert>;
  }

  return (
    <ProductGrid>
      <Items from={0} to={3} data={data} title={title} />
      <Items from={3} to={6} data={data} title={title} />

      <ProductGridItem $fullWidth>
        <Sheet
          as="aside"
          $tone="primary"
          aria-label={t('sheetAssignmentHeading', { ns: 'search' })}
        >
          <Columns $spacing={2} $valign="center" $clearLtMd>
            <Column $grow>
              <MaxWidth $maxWidth="780px">
                <Stack>
                  <StackSmall>
                    <Label>{t('sheetAssignmentLabel', { ns: 'search' })}</Label>
                  </StackSmall>
                  <StackSmall>
                    <H2 as="div" $noMargin>
                      {t('sheetAssignmentHeading', { ns: 'search' })}
                    </H2>
                  </StackSmall>
                </Stack>
                <Stack>
                  <Content>
                    <p>{t('sheetAssignmentBody', { ns: 'search' })}</p>
                  </Content>
                </Stack>
              </MaxWidth>
            </Column>
            <Column $noShrink>
              <Button
                as={Link}
                to={`/${resolveCarValuationRoute()}`}
                title={t('sheetAssignmentButtonTitle', { ns: 'search' })}
                data-wayke-action="valuation-push"
                $tone="primary"
              >
                {t('sheetAssignmentButtonText', { ns: 'search' })}
              </Button>
            </Column>
          </Columns>
        </Sheet>
      </ProductGridItem>

      <Items from={6} to={9} data={data} title={title} />
      <Items from={9} to={12} data={data} title={title} />

      <Items from={12} to={15} data={data} title={title} />
      <Items from={15} to={18} data={data} title={title} />

      <Items from={18} to={21} data={data} title={title} />
      <Items from={21} to={25} data={data} title={title} />
    </ProductGrid>
  );
};

export default ResultSetContainer;
